import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import { useTar } from '@/modules'
import { CHART_GREEN, CHART_STEEL, CHART_AXIS_TEXT, CHART_GRID_LINE, CHART_AXIS_LINE, CHART_AMBER } from './chartChrome'

// Mean and median of a 1-10 grade histogram (index i holds the count of grade i+1).
// Returns null when there are no graded challenges. The median of an even count
// is the midpoint of the two middle grades, so it can land on a half grade.
const summarizeGradeHistogram = (distribution: number[]): { mean: number; median: number } | null => {
  const total = distribution.reduce((sum, count) => sum + count, 0)
  if (total === 0) return null

  let weightedSum = 0
  for (let i = 0; i < distribution.length; i++) {
    weightedSum += (i + 1) * distribution[i]
  }

  const gradeAtPosition = (position: number): number => {
    let seen = 0
    for (let i = 0; i < distribution.length; i++) {
      seen += distribution[i]
      if (seen >= position) return i + 1
    }
    return distribution.length
  }
  const median = total % 2 === 1
    ? gradeAtPosition((total + 1) / 2)
    : (gradeAtPosition(total / 2) + gradeAtPosition(total / 2 + 1)) / 2

  return { mean: weightedSum / total, median }
}

interface GradeDistributionChartProps {
  // Index i holds the count of challenges graded i+1 (ten buckets).
  distribution: number[]
  className?: string
}

// Bar histogram of 1-10 grades with dashed average/median markers. Always LTR:
// the axis is numeric data, not translated copy.
export const GradeDistributionChart: React.FC<GradeDistributionChartProps> = ({ distribution, className }) => {
  const { t } = useTar()
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const summary = summarizeGradeHistogram(distribution)

    const instance = echarts.init(containerRef.current)

    const labels = Array.from({ length: 10 }, (_, i) => String(i + 1))

    // Category axis positions are zero-based indices, so grade g sits at g - 1.
    // Fractional indices are interpolated between the bars, which lets the
    // average and a half-grade median land between two columns.
    const gradeToAxisIndex = (grade: number) => grade - 1

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: labels,
        name: t('admin.statisticsGradeDistributionXAxis'),
        nameTextStyle: { color: CHART_AXIS_TEXT },
        axisLabel: { color: CHART_AXIS_TEXT },
        axisLine: { lineStyle: { color: CHART_AXIS_LINE } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: t('admin.statisticsGradeDistributionYAxis'),
        minInterval: 1,
        nameTextStyle: { color: CHART_AXIS_TEXT },
        axisLabel: { color: CHART_AXIS_TEXT },
        splitLine: { lineStyle: { color: CHART_GRID_LINE } },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          name: t('admin.statisticsGradeDistributionSeries'),
          type: 'bar',
          data: distribution,
          barMaxWidth: 40,
          itemStyle: { color: CHART_GREEN, borderRadius: [4, 4, 0, 0] },
          markLine: summary
            ? {
                symbol: 'none',
                animation: false,
                silent: true,
                lineStyle: { type: 'dashed', width: 2 },
                label: {
                  position: 'insideEndTop',
                  color: '#000',
                  fontSize: 12
                },
                data: [
                  {
                    xAxis: gradeToAxisIndex(summary.mean),
                    lineStyle: { color: CHART_AMBER },
                    label: {
                      formatter: t('admin.statisticsGradeDistributionAverageLabel', {
                        value: summary.mean.toFixed(1)
                      })
                    }
                  },
                  {
                    xAxis: gradeToAxisIndex(summary.median),
                    lineStyle: { color: CHART_STEEL },
                    label: {
                      position: 'insideEndBottom',
                      formatter: t('admin.statisticsGradeDistributionMedianLabel', {
                        value: Number.isInteger(summary.median) ? String(summary.median) : summary.median.toFixed(1)
                      })
                    }
                  }
                ]
              }
            : undefined
        }
      ]
    }

    instance.setOption(option)

    const handleResize = () => {
      instance.resize()
    }
    window.addEventListener('resize', handleResize)
    // echarts.init reads the container size synchronously; when the chart's tab
    // has just mounted (or the layout width hasn't settled) that read is 0 and
    // nothing paints until a resize. The window listener never fires on mount,
    // so observe the container directly — ResizeObserver fires with the real
    // size right after observe() and on every later change, fixing first paint.
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(instance.getDom())

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
      instance.dispose()
    }
  }, [distribution, t])

  return <div ref={containerRef} dir="ltr" className={className} />
}
